import { useCallback, useEffect, useState } from "react";
import { Clock, Coffee, Delete, Download, Lock, LogIn, LogOut, Share, Users, WifiOff, X } from "lucide-react";
import { usePWAManifest } from "@/hooks/usePWAManifest";
import { api } from "@/lib/api";

const DEVICE_KEY = "bk_clock_device";
const clockApi = (token) => ({
  get: (p) => api.get(p, { headers: { "X-Clock-Token": token } }),
  post: (p, body) => api.post(p, body, { headers: { "X-Clock-Token": token } }),
});

const fmtHours = (h) => {
  const m = Math.round((h || 0) * 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

function Keypad({ value, onChange, onSubmit, disabled, testPrefix }) {
  const press = (k) => {
    if (disabled) return;
    if (k === "back") return onChange(value.slice(0, -1));
    if (k === "ok") return onSubmit();
    if (value.length < 8) onChange(value + k);
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "back", "0", "ok"];
  return (
    <div className="grid grid-cols-3 gap-3 w-full max-w-xs mx-auto" data-testid={`${testPrefix}-keypad`}>
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => press(k)}
          disabled={disabled}
          data-testid={`${testPrefix}-key-${k}`}
          className={`h-16 rounded-2xl text-2xl font-black active:scale-95 transition-transform disabled:opacity-40 ${
            k === "ok"
              ? "bg-brand-yellow text-stone-900"
              : k === "back"
              ? "bg-stone-800 text-stone-300"
              : "bg-stone-800 text-white"
          }`}
        >
          {k === "back" ? <Delete className="w-6 h-6 mx-auto" /> : k === "ok" ? "GO" : k}
        </button>
      ))}
    </div>
  );
}

function Dots({ count, max = 4, testId }) {
  return (
    <div className="flex justify-center gap-3 my-6" data-testid={testId}>
      {Array.from({ length: Math.max(max, count) }).map((_, i) => (
        <span
          key={i}
          className={`w-4 h-4 rounded-full border-2 border-brand-yellow ${i < count ? "bg-brand-yellow" : "bg-transparent"}`}
        />
      ))}
    </div>
  );
}

function UnlockScreen({ onUnlocked }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!code || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/clock/unlock", { code, device_label: navigator.userAgent.slice(0, 60) });
      localStorage.setItem(DEVICE_KEY, data.token);
      onUnlocked(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't unlock — check the connection.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6" data-testid="clock-unlock-screen">
      <Lock className="w-10 h-10 text-brand-yellow mb-3" />
      <h1 className="text-2xl font-black text-white">Set up this device</h1>
      <p className="text-stone-400 text-sm text-center mt-1 max-w-xs">
        Enter the clock PIN (or the admin / cashier passcode) once. After that, staff just use their own PIN.
      </p>
      <Dots count={code.length} max={4} testId="clock-unlock-dots" />
      {error && <div className="text-red-400 text-sm mb-3" data-testid="clock-unlock-error">{error}</div>}
      <Keypad value={code} onChange={setCode} onSubmit={submit} disabled={busy} testPrefix="clock-unlock" />
    </div>
  );
}

function ResultCard({ result, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 7000);
    return () => clearTimeout(t);
  }, [onDone]);
  const meta = {
    in: { label: "Clocked in", bg: "bg-emerald-500", Icon: LogIn },
    out: { label: "Clocked out", bg: "bg-brand-yellow", Icon: LogOut },
    break_start: { label: "Break started", bg: "bg-sky-400", Icon: Coffee },
    break_end: { label: "Back from break", bg: "bg-emerald-500", Icon: LogIn },
  }[result.action] || { label: result.action, bg: "bg-stone-200", Icon: Clock };
  return (
    <button
      type="button"
      onClick={onDone}
      data-testid="clock-result-card"
      className={`w-full max-w-xs mx-auto rounded-3xl p-6 text-left ${meta.bg} text-stone-900 animate-in fade-in zoom-in-95`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-black opacity-80">
        <meta.Icon className="w-4 h-4" />
        {meta.label} · {fmtTime(result.at)}
      </div>
      <div className="text-3xl font-black mt-1" data-testid="clock-result-name">{result.employee_name}</div>
      {result.action === "out" && (
        <div className="text-sm font-bold mt-1">
          This shift: {fmtHours(result.hours)}{result.break_minutes ? ` (after ${result.break_minutes}m break)` : ""}
        </div>
      )}
      {result.action === "break_end" && (
        <div className="text-sm font-bold mt-1">Break: {result.break_minutes} min (unpaid)</div>
      )}
      {result.action === "break_start" && (
        <div className="text-sm font-bold mt-1">Enter your PIN again when you&apos;re back.</div>
      )}
      <div className="text-sm mt-3 font-semibold" data-testid="clock-result-hours">
        Today: {fmtHours(result.today_hours)} · This week: {fmtHours(result.week_hours)}
      </div>
      <div className="text-[10px] uppercase tracking-widest opacity-60 mt-3">Tap to dismiss</div>
    </button>
  );
}

function ChoiceScreen({ lookup, onChoose, onCancel, busy }) {
  const onBreak = lookup.state === "break";
  return (
    <div className="w-full max-w-xs mx-auto" data-testid="clock-choice-screen">
      <div className="text-[11px] uppercase tracking-widest text-stone-400 font-bold">
        {onBreak ? `On break since ${fmtTime(lookup.break_start)}` : `Clocked in since ${fmtTime(lookup.clock_in)}`}
      </div>
      <div className="text-3xl font-black text-white mt-1" data-testid="clock-choice-name">{lookup.employee_name}</div>
      <div className="text-stone-400 text-sm mt-1">Today so far: {fmtHours(lookup.today_hours)}</div>
      <div className="grid gap-3 mt-6">
        {onBreak ? (
          <button type="button" disabled={busy} onClick={() => onChoose("break_end")} data-testid="clock-choice-break-end"
            className="h-20 rounded-2xl bg-emerald-500 text-stone-900 text-xl font-black flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
            <LogIn className="w-6 h-6" /> End break
          </button>
        ) : (
          <button type="button" disabled={busy} onClick={() => onChoose("break_start")} data-testid="clock-choice-break-start"
            className="h-20 rounded-2xl bg-sky-400 text-stone-900 text-xl font-black flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
            <Coffee className="w-6 h-6" /> Start break
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => onChoose("out")} data-testid="clock-choice-out"
          className="h-20 rounded-2xl bg-brand-yellow text-stone-900 text-xl font-black flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
          <LogOut className="w-6 h-6" /> Clock out
        </button>
        <button type="button" onClick={onCancel} data-testid="clock-choice-cancel"
          className="h-12 rounded-2xl text-stone-400 text-sm font-bold uppercase tracking-widest">
          Cancel
        </button>
      </div>
    </div>
  );
}

function OnClockList({ people }) {
  return (
    <div className="w-full max-w-xs mx-auto mt-6" data-testid="clock-on-clock-list">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-2">
        <Users className="w-3.5 h-3.5" /> On the clock ({people.length})
      </div>
      {people.length === 0 ? (
        <div className="text-stone-500 text-sm">Nobody's clocked in.</div>
      ) : (
        <ul className="space-y-1.5">
          {people.map((p) => (
            <li
              key={`${p.name}-${p.clock_in}`}
              data-testid={`clock-on-clock-${(p.name || "").replace(/\s+/g, "-")}`}
              className={`flex justify-between items-center gap-2 text-sm rounded-xl px-3 py-2 ${p.overdue ? "bg-red-900/60 border border-red-500" : "bg-stone-800/70"}`}
            >
              <span className="text-white font-bold flex items-center gap-2">
                {p.name}
                {p.on_break && <span className="text-[9px] uppercase tracking-widest bg-sky-400 text-stone-900 rounded-full px-1.5 py-0.5">Break</span>}
                {p.overdue && <span className="text-[9px] uppercase tracking-widest bg-red-500 text-white rounded-full px-1.5 py-0.5">Past cutoff</span>}
              </span>
              <span className="text-stone-400 text-xs text-right">
                {p.on_break ? `break since ${fmtTime(p.break_start)}` : `since ${fmtTime(p.clock_in)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PunchScreen({ token, onLocked }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [onClock, setOnClock] = useState([]);
  const [offline, setOffline] = useState(!navigator.onLine);

  const refresh = useCallback(async () => {
    try {
      const { data } = await clockApi(token).get("/clock/status");
      setOnClock(data.on_clock || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(DEVICE_KEY);
        onLocked();
      }
    }
  }, [token, onLocked]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [refresh]);

  const fail = useCallback((err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(DEVICE_KEY);
      onLocked();
      return;
    }
    setError(err?.response?.data?.detail || (navigator.onLine ? "Punch failed — try again." : "No connection — punch not saved."));
  }, [onLocked]);

  const act = useCallback(async (pinValue, action) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await clockApi(token).post("/clock/action", { pin: pinValue, action });
      setResult(data);
      setOnClock(data.on_clock || []);
      setLookup(null);
    } catch (err) {
      fail(err);
      setLookup(null);
    } finally {
      setPin("");
      setBusy(false);
    }
  }, [token, fail]);

  const submit = useCallback(async () => {
    if (pin.length !== 4 || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await clockApi(token).post("/clock/lookup", { pin });
      if (data.state === "off") {
        setBusy(false);
        await act(pin, "in");
        return;
      }
      setLookup({ ...data, pin });
    } catch (err) {
      fail(err);
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [pin, busy, token, act, fail]);

  useEffect(() => {
    if (pin.length === 4 && !lookup) submit();
  }, [pin, lookup, submit]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-8" data-testid="clock-punch-screen">
      {offline && (
        <div className="flex items-center gap-2 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4" data-testid="clock-offline-banner">
          <WifiOff className="w-4 h-4" /> No connection — punches need signal
        </div>
      )}
      {result ? (
        <ResultCard result={result} onDone={() => setResult(null)} />
      ) : lookup ? (
        <ChoiceScreen
          lookup={lookup}
          busy={busy}
          onChoose={(action) => act(lookup.pin, action)}
          onCancel={() => { setLookup(null); setPin(""); }}
        />
      ) : (
        <>
          <h1 className="text-2xl font-black text-white">Enter your PIN</h1>
          <p className="text-stone-400 text-sm mt-1">Clock in, take a break, or clock out.</p>
          <Dots count={pin.length} testId="clock-pin-dots" />
          {error && <div className="text-red-400 text-sm mb-3" data-testid="clock-punch-error">{error}</div>}
          <Keypad value={pin} onChange={(v) => setPin(v.slice(0, 4))} onSubmit={submit} disabled={busy || offline} testPrefix="clock-pin" />
        </>
      )}
      <OnClockList people={onClock} />
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-right" data-testid="clock-live-time">
      <div className="text-xl font-black text-white tabular-nums leading-none whitespace-nowrap">
        {now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 whitespace-nowrap">
        {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
      </div>
    </div>
  );
}

function InstallButton() {
  const pwa = usePWAManifest({ manifest: "/manifest-clock.json", themeColor: "#1C1917", appleTitle: "BK Clock" });
  const [showHelp, setShowHelp] = useState(false);
  if (pwa.isStandalone) return null;
  const onClick = async () => {
    if (pwa.installEvent) {
      const ok = await pwa.install();
      if (!ok) setShowHelp(true);
    } else {
      setShowHelp(true);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="clock-install-btn"
        className="flex items-center gap-1.5 bg-brand-yellow text-stone-900 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black active:scale-95"
      >
        <Download className="w-3.5 h-3.5" /> Install
      </button>
      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setShowHelp(false)} data-testid="clock-install-help">
          <div className="bg-stone-800 text-white rounded-3xl p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-black text-lg flex items-center gap-2"><Download className="w-5 h-5 text-brand-yellow" /> Install BK Clock</div>
              <button type="button" onClick={() => setShowHelp(false)} aria-label="Close" className="text-stone-400"><X className="w-5 h-5" /></button>
            </div>
            {pwa.isIOS ? (
              <ol className="text-sm text-stone-200 list-decimal pl-5 space-y-2">
                <li>Tap the <Share className="w-4 h-4 inline -mt-1" /> <b>Share</b> button in Safari&apos;s toolbar.</li>
                <li>Scroll and tap <b>Add to Home Screen</b>.</li>
                <li>Tap <b>Add</b>. Launch <b>BK Clock</b> from your home screen — it stays unlocked on this device.</li>
              </ol>
            ) : (
              <ol className="text-sm text-stone-200 list-decimal pl-5 space-y-2">
                <li>Open the browser menu (<b>⋮</b> top-right in Chrome).</li>
                <li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
                <li>Launch <b>BK Clock</b> from your home screen — it stays unlocked on this device.</li>
              </ol>
            )}
            <div className="text-[11px] text-stone-400">Already installed? Open it from the home screen instead of the browser.</div>
          </div>
        </div>
      )}
    </>
  );
}

export default function ClockPage() {
  const [token, setToken] = useState(() => localStorage.getItem(DEVICE_KEY) || "");
  return (
    <div className="min-h-screen bg-stone-900 flex flex-col select-none" data-testid="clock-page" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="flex items-center justify-between px-4 py-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-6 h-6 text-brand-yellow shrink-0" />
          <div className="min-w-0">
            <div className="text-white font-black leading-none whitespace-nowrap">BK Clock</div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 whitespace-nowrap truncate">Blossom&apos;s Kitchen</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <InstallButton />
          <LiveClock />
        </div>
      </header>
      {token ? <PunchScreen token={token} onLocked={() => setToken("")} /> : <UnlockScreen onUnlocked={setToken} />}
    </div>
  );
}
